/**
 * News text (UX-1047 "fetstil, listor, stycken"): **bold**, a line starting with "- " is a list item, a blank line starts
 * a new paragraph. Parsed into a small tree that the pages render as elements – never as HTML – so whatever the
 * author typed (including tags) is shown as text.
 */
export type Inline = { bold: boolean; text: string };
export type Block = { kind: 'p'; lines: Inline[][] } | { kind: 'ul'; items: Inline[][] };

export function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push({ bold: false, text: s.slice(last, m.index) });
    out.push({ bold: true, text: m[1]! });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ bold: false, text: s.slice(last) });
  return out;
}

export function parseBody(md: string): Block[] {
  const blocks: Block[] = [];
  for (const chunk of md.replace(/\r/g, '').split(/\n\s*\n/)) {
    let para: Inline[][] = [];
    let list: Inline[][] = [];
    const flushP = () => {
      if (para.length) blocks.push({ kind: 'p', lines: para });
      para = [];
    };
    const flushL = () => {
      if (list.length) blocks.push({ kind: 'ul', items: list });
      list = [];
    };
    for (const line of chunk.split('\n').filter((l) => l.trim())) {
      const m = /^\s*[-•*]\s+(.*)$/.exec(line);
      if (m && !/^\s*\*\*/.test(line)) {
        flushP();
        list.push(parseInline(m[1]!));
      } else {
        flushL();
        para.push(parseInline(line.trim()));
      }
    }
    flushP();
    flushL();
  }
  return blocks;
}

export interface NewsAudience {
  internal: boolean;
  crew: boolean;
  clients: 'none' | 'all' | 'selected';
  chips: string;
  words: string;
}

export interface PostView {
  id: string;
  title: string;
  body: string;
  excerpt: string;
  author: { id: string; name: string };
  /** kff = news from the company · hub = something new in the Hub */
  category: 'kff' | 'hub';
  /** "New in the Hub" for these roles; null = every role */
  roles?: { keys: string[]; words: string } | null;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  pinned: boolean;
  publishedAt: string | null;
  publishAt: string | null;
  editedAt: string | null;
  archivedAt: string | null;
  updatedAt: string;
  project: { id: string; name: string } | null;
  link: { url: string; label: string } | null;
  image: { fileId: string } | null;
  unread: boolean;
  canManage: boolean;
  audience?: NewsAudience;
  seen?: { n: number; m: number; names: string[]; text: string };
}

export interface FeedView {
  canPost: boolean;
  unread: number;
  feed: PostView[];
  drafts: PostView[];
  archived: PostView[];
  tomorrow: string;
}

const fmt = (iso: string, lang: 'en' | 'sv', withYear = false) =>
  new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { timeZone: 'Europe/Stockholm', day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) }).format(new Date(iso));

/** "7 Oct" this year, "7 Oct 2025" otherwise. */
export const postDate = (iso: string, lang: 'en' | 'sv' = 'en', today = new Date()): string => fmt(iso, lang, new Date(iso).getUTCFullYear() !== today.getUTCFullYear());

/** Audience chooser state → API payload. */
export const audienceOf = (internal: boolean, crew: boolean) => ({ internal, crew, clients: 'none' as const });

/** "Visible to: in-house staff and freelancers & crew" (the composer's live line). */
export function visibleLine(internal: boolean, crew: boolean, lang: 'en' | 'sv' = 'en'): string {
  const sv = lang === 'sv';
  const parts = [internal ? (sv ? 'anställda' : 'in-house staff') : '', crew ? (sv ? 'frilansare och crew' : 'freelancers & crew') : ''].filter(Boolean);
  if (!parts.length) return sv ? 'Välj vem som ska se den.' : 'Choose who should see it.';
  return sv ? `Syns för: ${parts.join(' och ')}` : `Visible to: ${parts.join(' and ')}`;
}

/** The Now line (UX-1047): "New post: {title} · Read" or "2 new posts · {title}". */
export function nowLine(unread: PostView[], lang: 'en' | 'sv' = 'en'): string | null {
  if (!unread.length) return null;
  const t = unread[0]!.title.length > 34 ? unread[0]!.title.slice(0, 33).trim() + '…' : unread[0]!.title;
  if (lang === 'sv') return unread.length === 1 ? `Ny nyhet: ${t}` : `${unread.length} nya nyheter · ${t}`;
  return unread.length === 1 ? `New post: ${t}` : `${unread.length} new posts · ${t}`;
}

/** The start-up popup: unread posts, "New in the Hub" before "From KFF", newest first within each, at most `max` shown. */
export function sinceLastVisit(feed: PostView[], max = 6): { hub: PostView[]; kff: PostView[]; more: number; ids: string[] } {
  const unread = feed.filter((x) => x.unread).sort((a, b) => String(b.publishedAt).localeCompare(String(a.publishedAt)));
  const ordered = [...unread.filter((x) => x.category === 'hub'), ...unread.filter((x) => x.category !== 'hub')];
  const shown = ordered.slice(0, max);
  return { hub: shown.filter((x) => x.category === 'hub'), kff: shown.filter((x) => x.category !== 'hub'), more: ordered.length - shown.length, ids: unread.map((x) => x.id) };
}
